/**
 * <PauseRecapStep> — l'étape « Récapitulatif » du parcours de pause (organism).
 *
 * Le corps de la seconde étape du legacy (`ParentPauseSubscriptionModal.js:621-699`) : les quatre
 * lignes de ce qui a été choisi, ce que la pause décale de l'engagement, puis — au-delà du seuil
 * de perte de créneau — l'accord à donner avant de pouvoir confirmer. La phrase du quota du legacy
 * (« … consommera 21 jours sur ses 124 jours annuels. Il lui restera 103 jours après. ») n'y est
 * plus (Wajih, 30/09) : le reste à prendre poussait aux pauses longues.
 *
 * Contrôlé : l'accord est une prop (`ackAccepted`), le besoin d'accord aussi (`ackRequired`). Le
 * seuil lui-même n'est pas ici — il arrive en libellé, parce que c'est un chiffre métier.
 *
 * Les deux dates sont mises en forme par `dateFormatter` : elles arrivent en `YYYY-MM-DD`, comme
 * partout dans le parcours, et c'est le composant qui les écrit.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Checkbox } from "../../atoms/Checkbox"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SummaryRow } from "../../molecules/SummaryRow"
import { type UIPauseRecap } from "../../../ports/primary/ui/types"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { formatDayMonthYear } from "../../../ports/primary/ui/formatters/dateFormatter"

export type PauseRecapStepProps = {
    childFirstname: string
    /** `YYYY-MM-DD`. */
    startDate: string
    resumeDate: string
    /** « 21 jours ». */
    durationLabel: string
    /** Le motif choisi, tel qu'il se lit — « Vacances ». */
    reasonLabel: string

    /** Ce que la pause décale de l'engagement. Absent ou `null` (pas d'engagement en cours) → rien. */
    recap?: UIPauseRecap | null

    /** La pause dépasse le seuil de perte de créneau : l'accord conditionne la confirmation. */
    ackRequired?: boolean
    /** « 1 mois » — le seuil, tel que le parent le lit. */
    ackThresholdLabel?: string | null
    ackAccepted?: boolean
    onChangeAck?: (next: boolean) => void

    /** L'échec de l'envoi, avec la raison du serveur quand il en donne une. */
    errorMessage?: string | null

    testID?: string
}

export function PauseRecapStep(props: PauseRecapStepProps) {
    const {
        childFirstname,
        startDate,
        resumeDate,
        durationLabel,
        reasonLabel,
        recap,
        ackRequired = false,
        ackThresholdLabel,
        ackAccepted = false,
        onChangeAck,
        errorMessage,
        testID,
    } = props

    const threshold = ackThresholdLabel != null && ackThresholdLabel !== "" ? `supérieure à ${ackThresholdLabel} ` : ""

    return (
        <Stack gap="4" testID={testID}>
            {/* Le ton de la page pour les faits, la lavande pour ce qu'ils impliquent : dans le
                thème parent, `surface-subtle` EST la lavande, donc deux encarts lavande l'un sous
                l'autre se liraient comme un seul bloc. */}
            <Card background="surface" p="4">
                <Stack gap="2">
                    <SummaryRow label="Du" value={formatDayMonthYear(startDate)} />
                    <SummaryRow label="Reprise le" value={formatDayMonthYear(resumeDate)} />
                    <SummaryRow label="Durée" value={durationLabel} />
                    <SummaryRow label="Motif" value={reasonLabel} />
                </Stack>
            </Card>

            {recap != null && (
                <Card background="primary-soft" p="4">
                    <Text size="text-sm">
                        {`L'engagement ${elide(childFirstname)} se termine le `}
                        <Text size="text-sm" weight="bold">
                            {recap.commitmentEndLabel}
                        </Text>
                        {". Il sera décalé au "}
                        <Text size="text-sm" weight="bold">
                            {recap.commitmentShiftedLabel}
                        </Text>
                        {" après la pause."}
                    </Text>
                </Card>
            )}

            {ackRequired && (
                <Card background="warningLight" leftBorderColor="warning" p="4">
                    <Stack gap="3">
                        <Banner
                            icon="exclamation-circle"
                            color="warning-strong"
                            align="start"
                            message={`Une pause ${threshold}implique la libération de votre créneau. Nous ne pouvons pas garantir le retour avec le même tuteur à la reprise.`}
                            style={styles.warning}
                        />
                        <Checkbox
                            checked={ackAccepted}
                            onChange={(next) => onChangeAck?.(next)}
                            label="J'ai compris et j'accepte de libérer mon créneau"
                        />
                    </Stack>
                </Card>
            )}

            {errorMessage != null && errorMessage !== "" && (
                <Banner message={errorMessage} color="danger" icon="exclamation-circle" tinted />
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** La phrase se replie dans la carte au lieu de pousser le glyphe hors du cadre. */
    warning: { alignItems: "flex-start" },
}))
